import { Injectable } from '@angular/core';
import { User } from '../models/user';
import { isGuestUser } from '../models/guest-user';
import { CustomerService } from './customer.service';
import { UserService } from './user.service';

/**
 * Resolves the default landing route for a Client Portal user.
 *
 * Place Order was historically the fixed landing page, but it is now gated by the
 * per-user "Allow Place Orders" permission (XD-58844). When it is disabled the user
 * must land on the first page they can access in left-nav order below "What's New".
 * The cascade below mirrors the sidebar item order and each item's visibility
 * predicate exactly - the nine inet entries below "What's New" plus the worker Work
 * entry - so the resolver never sends a user to a page the sidebar hides or the
 * PermissionGuard blocks.
 */
@Injectable({
	providedIn: 'root'
})
export class NavigationService {

	constructor(private _userService: UserService,
				private _customerService: CustomerService) {
	}

	resolveDefaultLanding(user?: User): string {
		const u = user ?? this._userService.getUser();
		const iu = u?.inetUser;
		const notHold = this._customerService.getSelectedCustomer()?.billingStatus !== 'CREDIT_HOLD';

		// Every sidebar item below carries `user?.type === 'inet'`, so the cascade only
		// applies to inet users. The SSO relay resolves straight off a raw localStorage
		// payload, so the type is checked here rather than relying on each call site.
		if (u?.type === 'worker') {
			// sidebar.html:135 and permission-guard.service.ts:53 both gate /work on the
			// signed-in driver's canViewManifests, so mirror that here rather than handing
			// back a route the guard would then cancel.
			const currentDriver = u?.driverId ? u?.associatedDrivers?.find((ad) => ad.id === u.driverId) : null;
			return currentDriver?.canViewManifests ? 'work' : 'home';
		}
		if (u?.type !== 'inet') { return 'home'; }

		if (iu?.allowPlaceOrders && notHold) { return 'place-order'; }
		// XD-62456: the guest's sidebar is Place Order only, so nothing below it is a landing page for a guest,
		// whatever its (possibly drifted) permission flags say.
		if (isGuestUser(u)) { return 'home'; }
		if (iu?.allowTrackOrder && notHold) { return 'track-order'; }
		if (iu?.viewAccountReports && notHold) { return 'reports'; }
		if (iu?.viewRouteStops && u?.licenses?.xRouteEnabled && notHold) { return 'route-tracking'; }
		if ((iu?.allowTrackOrder || u?.licenses?.xRouteEnabled) && notHold) { return 'track-parcel'; }
		if (iu?.allowAddressBookAccess && notHold) { return 'address-book'; }
		if (u?.allowRecentTemplates && notHold) { return 'order-templates'; }
		if (u?.token && u?.globalSettings?.displayInvoices && iu?.viewInvoices) { return 'invoices'; }
		if (u?.token && u?.canViewOwnProfile && notHold) { return 'user-profile'; }

		return 'home';
	}
}
